"use client";
import { useUserContext } from "../../context/UserContext";
import { useEffect, useRef, useState } from "react";
import SearchBar from "../SearchBar/SearchBar";
import { MobileNavBar } from "../MobileNavBar/MobileNavBar";
import { getUserNotifications } from "@/services/NotificationsService";
import NotificationsContainer from "../Notifications/NotificationsContainer";
import { NotificationI } from "@/interfaces/Notifications";
import Inbox from "../Inbox/Inbox";
import { useChatContext } from "@/context/InboxContext";
import { handleOutsideClick } from "@/utils/Functions/General";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { useAuthModal } from "@/context/AuthModalContext";
import { UserView } from "./components/UserView";
import { ButtonWrapper } from "../Wrapper/ButtonWrapper/ButtonWrapper";
import { notifyAlert } from "@/utils/NotificationFunctions";
const messageIcon = "../../assets/images/icons/message-icon.svg";
const burgerIcon = "../../assets/images/icons/navbar-menu.svg";
const notificationIcon = "../../assets/images/icons/bell-icon.svg";
const logo = "../../assets/images/LOGO.svg";
const witnessLogo = "../../assets/images/witness-logo.svg";
const lightMode = "../../assets/images/icons/toggle-icon-light.svg";
const darkMode = "../../assets/images/icons/toggle-icon.svg";
const kalebLogoBackground = "../../assets/images/kaleb-logo-background.svg";

interface NavbarPropsI {
  theme: string;
  toggleTheme: () => void;
  googleApi: string;
}

function Navbar(props: NavbarPropsI) {
  const { theme, toggleTheme } = props;
  const { setAuthModal } = useAuthModal();
  const pathname = usePathname();
  const { currentUser, handleLogout, userToken, userLoader } = useUserContext();
  const [burgerMenu, setBurgerMenu] = useState<boolean>(false);
  const [showNotifications, setShowNotifications] = useState<boolean>(false);
  const dropdownRef: any = useRef(null);
  const burgerRef: any = useRef(null);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<Array<NotificationI>>([]);
  const [pendingNotifications, setPendingNotifications] =
    useState<boolean>(false);
  const { pendingChats, openInbox, setOpenInbox, inboxContainerRef } =
    useChatContext();
  const notificationsRef = useRef<any>(null);
  const notificationNavBtn = useRef<any>(null);
  const inboxNavBtnRef = useRef<any>(null);

  const getAllNotifications = async () => {
    if (currentUser) {
      let tempNotifs = await getUserNotifications(userToken, currentUser._id);
      checkForUnseenNotifs(tempNotifs);
      setNotifications(tempNotifs);
    }
  };

  const checkForUnseenNotifs = (notifs: Array<NotificationI>) => {
    const hasNewElement = notifs.some((obj: NotificationI) => obj.isNewElement);
    setPendingNotifications(hasNewElement);
  };

  const handleOutsideClickNotifications = (event: any) =>
    handleOutsideClick(
      event,
      notificationNavBtn,
      setShowNotifications,
      notificationsRef
    );
  const handleOutsideClickInbox = (event: any) =>
    handleOutsideClick(event, inboxContainerRef, setOpenInbox, inboxNavBtnRef);
  const handleOutsideClickUserMenu = (event: any) =>
    handleOutsideClick(event, dropdownRef, setBurgerMenu, burgerRef);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClickNotifications);
    document.addEventListener("mousedown", handleOutsideClickInbox);
    document.addEventListener("mousedown", handleOutsideClickUserMenu);
    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClickNotifications
      );
      document.removeEventListener("mousedown", handleOutsideClickInbox);
      document.removeEventListener("mousedown", handleOutsideClickUserMenu);
    };
  }, []);

  useEffect(() => {
    currentUser && getAllNotifications();
  }, [currentUser, userLoader]);

  useEffect(() => {
    setBurgerMenu(false);
  }, []);

  const handleAiDateResearchClick = () => {
    notifyAlert(
      "This function is temporarily disabled, because recently Google Lens released a similar feature for free, where you can easily AI-check the date of any photo online. In Chrome browser use Right click - Google Lens - input 'date of photo'."
    );
  };

  return (
    <div className="flex items-center w-full" style={{ paddingTop: 0 }}>
      {/* Notifications Container - Visible en desktop y mobile */}
      {currentUser && showNotifications && (
        <div className="fixed lgxl:top-48 max-lgxl:bottom-[90px] max-lgxl:left-1/2 max-lgxl:-translate-x-1/2 lgxl:right-0 z-50">
          <NotificationsContainer
            notificationsRef={notificationsRef}
            setShowNotifications={setShowNotifications}
            show={showNotifications}
            notifications={notifications}
            setNotifications={setNotifications}
            setPendingNotifications={setPendingNotifications}
          />
        </div>
      )}
      {/* Inbox - Visible en desktop y mobile */}
      {currentUser && openInbox && (
        <div
          className="flex flex-col w-full h-full fixed top-0 left-0 p-0 z-[100] pointer-events-none"
          ref={inboxContainerRef}
        >
          <div className="z-10 absolute max-lgxl:hidden top-[120px] right-[90px] -translate-y-full w-0 border-l-[20px] border-r-[20px] border-b-[20px] border-l-transparent border-r-transparent border-b-[#141416]" />
          <Inbox />
        </div>
      )}
      {/* Mobile Bottom Navigation Bar */}
      <div className="lgxl:hidden fixed bottom-0 left-0 w-full z-50">
        <MobileNavBar
          show={showSearch}
          inboxNavBtnRef={inboxNavBtnRef}
          notificationNavBtn={notificationNavBtn}
          setShowSearchBar={setShowSearch}
          setShowNotifications={setShowNotifications}
          showNotifications={showNotifications}
        />
      </div>

      {/* Desktop Navbar */}
      <nav
        className={`${theme} flex flex-col  w-full px-2 sm:px-4 lgxl:px-[30px]`}
      >
        {/* Theme Toggle - Desktop only */}
        <div className="absolute right-4 lgxl:right-[30px] top-4 hidden lgxl:block z-10">
          <img
            className="toggle-theme-btn cursor-pointer w-12 lgxl:w-[60px]"
            src={theme === "dark" ? lightMode : darkMode}
            alt="toggle theme"
            onClick={toggleTheme}
          />
        </div>
        <div className="flex w-full justify-between items-center gap-2 h-[150px] sm:gap-2 py-2">
          <div className="flex gap-0.5 items-center">
            <div className="max-w-[200px] relative min-w-[100px] flex h-auto ">
              <Link
                className={
                  "max-w-[200px] min-w-[100px] flex h-auto " +
                  (pathname === "/" ? "active" : "")
                }
                href="/"
              >
                <>
                  <img
                    src={logo}
                    alt="kalebGlory logo"
                    className="w-full cursor-pointer h-auto z-10"
                  />
                  {theme === "light" && (
                    <img
                      src={kalebLogoBackground}
                      alt="kalebGlory logo background"
                      className=" h-auto absolute z-5 top-1/2 w-full left-1/2 -translate-x-full -translate-2/5 pointer-events-none"
                    />
                  )}
                </>
              </Link>
            </div>
            <div className=" max-w-[200px] min-w-[100px] flex h-auto">
              <img
                src={witnessLogo}
                alt="make them witness"
                className={
                  "w-full h-auto " + (theme === "light" ? " invert" : "")
                }
              />
            </div>
          </div>
          {/* Desktop Search Bar */}
          <div className="flex-1 justify-center max-md:bottom-[60px] max-md:w-3/5 max-md:left-1/2 max-md:-translate-1/2 max-md:fixed z-10 w-full flex md:px-4">
            <SearchBar show={showSearch} />
          </div>
          <div className="gap-1 relative items-center hidden lgxl:flex">
            <ul
              className="flex gap-2 items-center relative"
              style={{ paddingLeft: 0 }}
            >
              <li className="absolute -bottom-16 left-0 flex gap-10 text-sm">
                <Link
                  href="/NFTprogram"
                  className="hover:opacity-80 p-3  transition-opacity whitespace-nowrap"
                >
                  NFT Program
                </Link>
                <Link
                  className={`hover:opacity-80 p-3  transition-opacity whitespace-nowrap ${
                    pathname === "/smart-search" ? "active" : ""
                  }`}
                  href="/smart-search"
                >
                  Smart search
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  className={`hover:opacity-80 p-3 transition-opacity whitespace-nowrap bg-transparent ${
                    pathname === "/date-research" ? "active" : ""
                  }`}
                  onClick={handleAiDateResearchClick}
                >
                  AI Date Research
                </button>
              </li>
              <li>
                <Link
                  className={`hover:opacity-80 p-3  transition-opacity whitespace-nowrap ${
                    pathname === "/about" ? "active" : ""
                  }`}
                  href="/about"
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  className={`hover:opacity-80 p-3  transition-opacity whitespace-nowrap ${
                    pathname === "/categories" ? "active" : ""
                  }`}
                  href="/categories"
                >
                  Categories
                </Link>
              </li>
              {currentUser === null ? (
                <>
                  <li className="px-2">
                    <ButtonWrapper
                      className="px-6 lg:px-8 py-2.5 lg:py-3 rounded-2xl hover:opacity-90 whitespace-nowrap text-sm lg:text-base"
                      onClick={() =>
                        setAuthModal({ show: true, form: "login" })
                      }
                    >
                      Login
                    </ButtonWrapper>
                  </li>
                  <li className="px-2">
                    <ButtonWrapper
                      className="px-6 lg:px-8 py-2.5 lg:py-3 rounded-2xl hover:opacity-90 whitespace-nowrap text-sm lg:text-base"
                      onClick={() =>
                        setAuthModal({ show: true, form: "signUp" })
                      }
                    >
                      Sign Up
                    </ButtonWrapper>
                  </li>
                </>
              ) : (
                <>
                  <li
                    className="w-[40px] min-w-[40px] mx-2 relative cursor-pointer"
                    ref={notificationNavBtn}
                    onClick={() => setShowNotifications(!showNotifications)}
                  >
                    <img src={notificationIcon} alt="bell icon" />
                    {pendingNotifications && (
                      <div className="w-[10px] absolute h-[10px] -top-2 -right-2 bg-[#45b26b] rounded-full" />
                    )}
                  </li>
                  <li
                    onClick={() => setOpenInbox(!openInbox)}
                    ref={inboxNavBtnRef}
                    className="w-[40px] min-w-[40px] mx-2  cursor-pointer relative"
                  >
                    {pendingChats > 0 && (
                      <div className="w-[20px] flex items-center justify-center absolute h-[20px] -top-[12px] left-[10px] bg-green-700 rounded-full">
                        {pendingChats}
                      </div>
                    )}
                    <img src={messageIcon} alt="mail icon" />
                  </li>
                  <li className="profile-btn cursor-pointer">
                    <UserView currentUser={currentUser} />
                  </li>
                </>
              )}
            </ul>
          </div>
          <div
            className={
              "flex lgxl:hidden pr-[20px] items-center cursor-pointer z-20 flex-shrink-0 ml-auto " +
              (theme === "light" ? " invert " : "")
            }
            ref={burgerRef}
            onClick={() => setBurgerMenu(!burgerMenu)}
          >
            <img src={burgerIcon} alt="menu" className="w-[32px] h-[32px]" />
          </div>
        </div>
      </nav>

      {/* Mobile Burger Menu Sidebar */}

      <div
        className={`fixed duration-300 ${
          burgerMenu
            ? "opacity-100 z-40"
            : "opacity-0 pointer-events-none -z-10"
        }`}
        onClick={() => setBurgerMenu(false)}
      />
      <div
        className={`fixed top-[150px] right-0 w-full ${
          theme === "light" ? "bg-white" : "bg-black-dark"
        } transition-all duration-300 ease-out ${
          burgerMenu
            ? " h-full z-10 opacity-100"
            : " h-0 pointer-events-none opacity-0"
        }`}
        ref={dropdownRef}
        onClick={(e) => e.stopPropagation()}
      >
        <ul className="flex flex-col items-center p-4 gap-2 w-full">
          <li className="w-full text-center">
            <Link
              href="/about"
              onClick={() => setBurgerMenu(false)}
              className={
                "block py-3  text-xl hover:text-purple-300 transition-colors " +
                (theme === "light" ? " text-black " : "text-white")
              }
            >
              About
            </Link>
          </li>

          {currentUser && currentUser.admin !== 0 && (
            <li className="w-full text-center">
              <Link
                href="/admin"
                onClick={() => setBurgerMenu(false)}
                className={
                  "block py-3 text-xl hover:text-purple-300 transition-colors " +
                  (theme === "light" ? " text-black " : "text-white")
                }
              >
                Admin
              </Link>
            </li>
          )}

          <li className="w-full text-center">
            <Link
              href="/categories"
              onClick={() => setBurgerMenu(false)}
              className={
                "block py-3 text-xl hover:text-purple-300 transition-colors " +
                (theme === "light" ? " text-black " : "text-white")
              }
            >
              Categories
            </Link>
          </li>

          <li className="w-full text-center">
            <Link
              href="/NFTprogram"
              onClick={() => setBurgerMenu(false)}
              className={
                "block py-3 text-xl hover:text-purple-300 transition-colors " +
                (theme === "light" ? " text-black " : "text-white")
              }
            >
              NFT Program
            </Link>
          </li>

          <li className="w-full text-center">
            <Link
              href="/smart-search"
              onClick={() => setBurgerMenu(false)}
              className={
                "block py-3 text-xl hover:text-purple-300 transition-colors " +
                (theme === "light" ? " text-black " : "text-white")
              }
            >
              Smart search
            </Link>
          </li>

          <li className="w-full text-center">
            <button
              type="button"
              onClick={() => {
                setBurgerMenu(false);
                handleAiDateResearchClick();
              }}
              className={
                "block w-full py-3 text-xl hover:text-purple-300 transition-colors text-center bg-transparent " +
                (theme === "light" ? " text-black " : "text-white")
              }
            >
              AI Date Research
            </button>
          </li>

          <li className="w-full text-center">
            <Link
              href="/upload"
              onClick={() => setBurgerMenu(false)}
              className={
                "block py-3 text-xl hover:text-purple-300 transition-colors " +
                (theme === "light" ? " text-black " : "text-white")
              }
            >
              Upload
            </Link>
          </li>

          {/* Auth Buttons or Sign Out */}
          {!currentUser ? (
            <div className="flex flex-col items-center gap-4 mt-8 w-full max-w-xs">
              <ButtonWrapper
                className="w-full py-3 rounded-lg hover:opacity-90 transition-all text-lg font-medium"
                onClick={() => {
                  setAuthModal({ show: true, form: "login" });
                  setBurgerMenu(false);
                }}
              >
                Login
              </ButtonWrapper>

              <ButtonWrapper
                className="w-full py-3 rounded-lg border border-white/20 hover:opacity-90 transition-all text-lg font-medium"
                onClick={() => {
                  setAuthModal({ show: true, form: "signUp" });
                  setBurgerMenu(false);
                }}
              >
                Sign Up
              </ButtonWrapper>
            </div>
          ) : (
            <li className="w-full text-center mt-8 flex items-center justify-center">
              <ButtonWrapper
                className="flex items-center justify-center px-16 py-3 rounded-lg hover:opacity-90 transition-all text-lg font-medium"
                onClick={() => {
                  handleLogout();
                  setBurgerMenu(false);
                }}
              >
                Sign out
              </ButtonWrapper>
            </li>
          )}
          <li className="mt-8">
            <button
              onClick={toggleTheme}
              className="flex items-center gap-3 p-3 rounded-lg"
              aria-label="Toggle theme"
            >
              <img
                src={theme === "dark" ? lightMode : darkMode}
                alt="toggle theme"
                className="w-[60px] h-[60px]"
              />
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default Navbar;
